import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Dialog } from "../ui/Dialog"
import { CheckboxItem } from "../ui/Checkbox"
import { Button } from "../ui/Button"
import { popDialog, pushDialog, resetDialogs } from "@/store/dialogs-state"
import { StyleInfoRecord } from "@/lib/catalog"
import {
	saveAsCurrentStyle,
	showPublishCurrentStyleDialog,
	showUpdateCurrentStyleDialog,
} from "@/store/style/style-state"
import { DialogProps } from "@radix-ui/react-dialog"
import { ConfirmDialog } from "./ConfirmDialog"
import { analytics } from "@/telemetry/amplitude"
import { useAtomValue } from "jotai"
import { projectAtom } from "@/store/project/projects-state"
import { getFinalizeAIStyleProperties } from "@/store/style/style-event-utils"

export function AddEditsToStyleDialog(
	props: Partial<DialogProps> & {
		style: StyleInfoRecord
		canCancel: boolean
		isStaffPlus?: boolean
	},
) {
	const { t } = useTranslation("components")
	// Public styles cannot be updated in-place, so keep the checkbox locked to "create new".
	const isPublicStyle = props.style.visibility === "public"
	const [createNewStyle, setCreateNewStyle] = useState<boolean>(isPublicStyle)
	const isStaffPlus = props.isStaffPlus ?? false
	const isSharedStyle =
		props.style.visibility === "public" || props.style.visibility === "internal"
	const project = useAtomValue(projectAtom)

	const trackAddFinalEdits = (updatedStyle: StyleInfoRecord) => {
		const newReferenceCount = Math.max(
			0,
			updatedStyle.referenceCount - props.style.referenceCount,
		)
		analytics.trackEvent({
			label: "add_final_edits",
			type: "event",
			customProperties: {
				projectId: project?.id ?? null,
				newReferenceCount,
			},
		})

		analytics.trackEvent({
			label: "finalize_ai_style",
			type: "event",
			customProperties: getFinalizeAIStyleProperties({
				projectStyleType: "My AI Style", // i18n-ignore: analytics value
				style: updatedStyle,
				projectId: project?.id ?? null,
				styleCreationMethod: "Final Edits", // i18n-ignore: analytics value
				styleStatus: createNewStyle ? "New" : "Existing",
			}),
		})
	}

	const handleAddEdits = () => {
		resetDialogs()

		if (createNewStyle) {
			showPublishCurrentStyleDialog(props.style, props, undefined, (result) => {
				void saveAsCurrentStyle(result.styleInfo, result.references, true)
				trackAddFinalEdits(result.styleInfo)
			})
		} else {
			void showUpdateCurrentStyleDialog(props.style, props, (result) => {
				void saveAsCurrentStyle(result.styleInfo, result.references, true)
				trackAddFinalEdits(result.styleInfo)
			})
		}
	}

	const handleConfirmAddEdits = () => {
		if (!isStaffPlus || !isSharedStyle || createNewStyle) {
			handleAddEdits()
			return
		}

		const visibilityLabel = props.style.visibility === "internal" ? "internal" : "public"

		pushDialog(ConfirmDialog, {
			title: t("addEditsToStyleDialog.editSharedTitle"),
			embeddedComponent: (
				<div className="mt-2 space-y-2 text-sm">
					<div>
						{t("addEditsToStyleDialog.aboutToUpdate")}
						<span className="font-semibold">
							{t("addEditsToStyleDialog.shared")}
						</span>{" "}
						{t("addEditsToStyleDialog.styleDescription", {
							visibilityLabel,
							name: props.style.name,
						})}
					</div>
					<div className="font-semibold text-yellow-500">
						{t("addEditsToStyleDialog.affectsAllUsers")}
					</div>
				</div>
			),
			secondary: { title: t("common:action.cancel") },
			primary: {
				title: t("common:action.proceed"),
				handler: () => {
					handleAddEdits()
				},
			},
			trackingViewName: "confirm_shared_style_edit",
		})
	}

	return (
		<Dialog trackingViewName="add_edits_to_style_dialog" contentSize="sm-h-auto" disableClose>
			<div className="flex w-full flex-col items-start justify-start space-y-6">
				<div className="flex flex-col space-y-3">
					<div className="text-start text-md font-bold">
						{t("addEditsToStyleDialog.title")}
					</div>
					<div className="flex flex-col space-y-0 text-sm">
						{t("addEditsToStyleDialog.description", { styleName: props.style.name })}
					</div>
					<CheckboxItem
						trackingLabel="create_new"
						aria-label={t("addEditsToStyleDialog.ariaCreateNew")}
						id="create_new"
						checkedState={createNewStyle}
						content={
							<div className="flex flex-row items-center space-x-1 text-base-300">
								{t("addEditsToStyleDialog.saveAsNew")}
							</div>
						}
						onCheckChanged={(_, isChecked) => {
							setCreateNewStyle(isChecked)
						}}
						disabled={isPublicStyle}
					/>
				</div>

				<div className="flex w-full flex-row justify-end space-x-2">
					{props.canCancel && (
						<Button trackingLabel="cancel" size="sm" onClick={() => popDialog()}>
							{t("common:action.cancel")}
						</Button>
					)}

					{!props.canCancel && (
						<Button trackingLabel="skip" size="sm" onClick={() => resetDialogs()}>
							{t("common:action.skip")}
						</Button>
					)}

					<Button
						trackingLabel="confirm"
						size="sm"
						variant="primary"
						onClick={handleConfirmAddEdits}
					>
						{t("addEditsToStyleDialog.addFinalEdits")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
